import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

// Keeps track of the insights being worked out again on the server, so a page loading insights straight after a change
// (as when the user comes back from correcting a node in its model) waits for them rather than showing the old ones.
// Each refresh is of a workspace, and a page waits only for those of the workspace it shows. Each refresh also moves
// the version on, so what was cached before it is known to be stale
@Injectable({ providedIn: 'root' })
export class InsightsRefreshService {
    version = 0;
    // each refresh under way, with the workspace it is of (undefined when that isn't known, which every page waits for)
    private pending = new Map<Promise<unknown>, string | undefined>();
    // pages waiting for a refresh to finish before they load, for what says why they are waiting
    private waits = 0;
    // a refresh or a wait started or finished, for what says the insights are being updated
    readonly changed = new Subject<void>();

    // a refresh from this page is under way: of the workspace given, or of any
    isRefreshing(workspaceKey?: string) {
        return this.forWorkspace(workspaceKey).length > 0;
    }

    get refreshing() {
        return this.pending.size > 0;
    }

    // a page is waiting for the insights it shows to be worked out
    get waiting() {
        return this.waits > 0;
    }

    track<T>(refresh: Promise<T>, workspaceKey?: string): Promise<T> {
        this.version++;
        this.pending.set(refresh, workspaceKey);
        this.changed.next();
        const done = () => {
            this.pending.delete(refresh);
            this.changed.next();
        };
        refresh.then(done, done);
        return refresh;
    }

    // resolves once every refresh under way of the workspace given (or of every workspace, without one) has finished,
    // however it ended. Refreshes of other workspaces don't change what that one shows, so aren't waited for
    async settled(workspaceKey?: string) {
        if (!this.forWorkspace(workspaceKey).length) return;
        this.waits++;
        this.changed.next();
        try {
            for (let left = this.forWorkspace(workspaceKey); left.length; left = this.forWorkspace(workspaceKey)) await Promise.allSettled(left);
        } finally {
            this.waits--;
            this.changed.next();
        }
    }

    private forWorkspace(workspaceKey?: string) {
        return [...this.pending.entries()].filter(([, w]) => !workspaceKey || !w || w === workspaceKey).map(([p]) => p);
    }
}
